import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Users, Pencil } from "lucide-react";
import { useNavigate } from "react-router-dom";

const EMPTY = { name: "", legal_name: "", gst_number: "", city: "", state: "", pin_code: "", billing_address: "" };

export default function Clients() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const nav = useNavigate();

  const load = async () => {
    const { data } = await api.get("/clients");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "", legal_name: c.legal_name || "", gst_number: c.gst_number || "",
      city: c.city || "", state: c.state || "", pin_code: c.pin_code || "",
      billing_address: c.billing_address || "",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/clients/${editing.id}`, form);
        toast.success("Client updated");
      } else {
        await api.post("/clients", form);
        toast.success("Client created");
      }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = items.filter((c) =>
    !q || c.name?.toLowerCase().includes(q.toLowerCase()) || c.code?.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div data-testid="page-clients" className="space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Master</div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 mt-1">Clients</h1>
        </div>
        <button data-testid="btn-new-client" onClick={openNew} className="bg-[var(--navy)] hover:bg-slate-800 text-white text-sm font-semibold px-4 py-2.5 rounded-md flex items-center gap-2">
          <Plus size={16} /> New Client
        </button>
      </div>

      <div className="flex gap-3">
        <input data-testid="input-client-search" placeholder="Search by name or code…" value={q} onChange={(e) => setQ(e.target.value)} className="flex-1 max-w-md px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 text-left">
              <th className="px-5 py-3">Code</th><th className="px-5 py-3">Name</th>
              <th className="px-5 py-3">GST</th><th className="px-5 py-3">City</th>
              <th className="px-5 py-3">Status</th><th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} data-testid={`client-row-${c.id}`} className="table-row border-t border-slate-100">
                <td className="px-5 py-3 font-mono text-xs">{c.code}</td>
                <td className="px-5 py-3 font-medium text-slate-900">{c.name}</td>
                <td className="px-5 py-3 text-slate-600">{c.gst_number || "-"}</td>
                <td className="px-5 py-3 text-slate-600">{c.city || "-"}</td>
                <td className="px-5 py-3"><span className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold">{c.status}</span></td>
                <td className="px-5 py-3 text-right space-x-4">
                  <button data-testid={`btn-edit-client-${c.id}`} onClick={() => openEdit(c)} className="text-xs text-slate-700 hover:text-slate-900 font-semibold inline-flex items-center gap-1"><Pencil size={12} /> Edit</button>
                  <button data-testid={`btn-manage-contacts-${c.id}`} onClick={() => nav(`/clients/${c.id}`)} className="text-xs text-amber-700 hover:text-amber-900 font-semibold inline-flex items-center gap-1"><Users size={13} /> Contacts</button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan="6" className="py-8 text-center text-slate-500">No clients found</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-40" onClick={() => setOpen(false)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg p-6 w-full max-w-lg" data-testid="dialog-client">
            <h2 className="font-heading text-xl font-bold mb-4">{editing ? `Edit ${editing.code}` : "New Client"}</h2>
            <div className="grid grid-cols-2 gap-3">
              {[
                ["name", "Client Name*", true], ["legal_name", "Legal Company Name"], ["gst_number", "GST Number"],
                ["city", "City"], ["state", "State"], ["pin_code", "PIN Code"],
              ].map(([k, l, req]) => (
                <div key={k} className={k === "name" ? "col-span-2" : ""}>
                  <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">{l}</label>
                  <input data-testid={`client-${k}`} required={req} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm" />
                </div>
              ))}
              <div className="col-span-2">
                <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">Billing Address</label>
                <textarea rows={2} value={form.billing_address || ""} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-sm border border-slate-300 rounded-md">Cancel</button>
              <button data-testid="btn-save-client" className="px-4 py-2 text-sm bg-[var(--navy)] text-white rounded-md">{editing ? "Save" : "Create"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
